import {toLower} from '@servicenow/ui-internal';
import {
	SEISMIC_HOIST_TAG_NAME,
	HOIST_ATTR,
	NOW_HOIST_ATTR,
	COMPONENT_ID_ATTRIBUTE,
	HOIST_APPEND_TO_ATTR,
	HOIST_APPEND_TO_PROP,
	NOW_HOIST_APPEND_TO_ATTR,
	NOW_HOIST_APPEND_TO_PROP,
	COMPONENT_NAME_ATTR,
	HOIST_FIRST_SCROLL_PARENT,
	NOW_HOIST_PREPEND_TO_PROP,
	NOW_HOIST_PREPEND_TO_ATTR,
	NOW_HOIST_PROP,
	NOW_MODE_ATTR,
	NOW_COMPONENT_NAME_ATTR
} from './constants';
import {
	ROOT_NODE,
	ATTACHED_ROOT_NODE,
	CONFIG,
	VARIABLES,
	COMPONENT_ID,
	PROPERTY_DEFS,
	PROPS,
	RETARGETED_ACTION_TYPES,
	GET_RESOLVED_PROPERTY
} from './symbols';
import {isHoisted, getHoistTarget, getHostVariables} from './utils';

class SeismicHoistElement extends HTMLElement {
	constructor() {
		super();

		const variables = {};
		this[VARIABLES] = variables;

		variables[RETARGETED_ACTION_TYPES] = [];
	}
}

const hoistTagName = SEISMIC_HOIST_TAG_NAME.toLowerCase();
window.customElements &&
	!window.customElements.get(hoistTagName) &&
	window.customElements.define(hoistTagName, SeismicHoistElement);

const cast = (sourceEl, name, value) =>
	sourceEl[VARIABLES][PROPERTY_DEFS][name] && sourceEl[VARIABLES][PROPERTY_DEFS][name].unstableParse
		? JSON.stringify(sourceEl[VARIABLES][PROPS][name])
		: value;

export function createTargetAttr(tagName, componentId) {
	return `${toLower(tagName)}[component-id="${componentId}"]`;
}

export function createTarget(sourceEl) {
	const targetEl = document.createElement(hoistTagName);
	const sourceVariables = getHostVariables(sourceEl);
	const targetVariables = getHostVariables(targetEl);
	const {
		[CONFIG]: {shadowRootOptions}
	} = sourceVariables;
	targetVariables[ATTACHED_ROOT_NODE] = targetEl.attachShadow(shadowRootOptions);
	targetEl.setAttribute(
		'target-for',
		createTargetAttr(sourceEl.tagName, sourceVariables[COMPONENT_ID])
	);
	const {attributes} = sourceEl;
	const {length: attributeCount} = sourceEl.attributes;
	for (let i = 0; i < attributeCount; i++) {
		const {name, value} = attributes[i];
		if (!omitAttrs.includes(name)) targetEl.setAttribute(name, cast(sourceEl, name, value));
	}

	targetEl.sourceElement = sourceEl;
	return targetEl;
}

export const omitAttrs = [
	NOW_MODE_ATTR,
	NOW_COMPONENT_NAME_ATTR,
	NOW_HOIST_ATTR,
	NOW_HOIST_APPEND_TO_ATTR,
	NOW_HOIST_PREPEND_TO_ATTR,
	HOIST_ATTR,
	HOIST_APPEND_TO_ATTR,
	COMPONENT_ID_ATTRIBUTE,
	COMPONENT_NAME_ATTR
];

const getParent = element => {
	const parent = element.parentNode;
	if (parent && parent.host) return parent.host;

	return parent;
};

const overflowRegex = /(auto|scroll)/;
const getFirstScrollParent = sourceEl => {
	let style = getComputedStyle(sourceEl);
	const excludeStaticParent = style.position === 'absolute';
	let parent = sourceEl;

	if (style.position === 'fixed') return document.body;

	while ((parent = getParent(parent)) !== null) {
		if (parent === document.body) break;

		style = getComputedStyle(parent);
		if (excludeStaticParent && style.position === 'static') continue;

		if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) return parent;
	}

	return document.body;
};

const shouldPrepend = sourceEl =>
	sourceEl[GET_RESOLVED_PROPERTY](NOW_HOIST_PREPEND_TO_PROP) ||
	sourceEl.getAttribute(NOW_HOIST_PREPEND_TO_ATTR);

const shouldAppend = sourceEl =>
	sourceEl[GET_RESOLVED_PROPERTY](HOIST_APPEND_TO_PROP) ||
	sourceEl.getAttribute(HOIST_APPEND_TO_ATTR) ||
	sourceEl[GET_RESOLVED_PROPERTY](NOW_HOIST_APPEND_TO_PROP) ||
	sourceEl.getAttribute(NOW_HOIST_APPEND_TO_ATTR);

const getAppendToEl = sourceEl => {
	// TODO: log and warn about legacy attributes, props
	const hoistAppendToSelector = shouldAppend(sourceEl);
	const hoistPrependToSelector = shouldPrepend(sourceEl);

	if (
		hoistAppendToSelector === HOIST_FIRST_SCROLL_PARENT ||
		hoistPrependToSelector === HOIST_FIRST_SCROLL_PARENT
	)
		return getFirstScrollParent(sourceEl);

	const selectorOrNode = hoistPrependToSelector || hoistAppendToSelector;
	if (!selectorOrNode) return document.body;

	const node =
		typeof selectorOrNode === 'string'
			? document.querySelector(selectorOrNode)
			: selectorOrNode instanceof HTMLElement
			? selectorOrNode
			: undefined;

	return node || document.body;
};

export function shouldHoistAttribute(host, name) {
	return isHoisted(host) && !omitAttrs.includes(name);
}

export function hoistAttribute(host, name, value) {
	const hostVariables = getHostVariables(host);
	const target = getHoistTarget(hostVariables);
	if (target.tagName === SEISMIC_HOIST_TAG_NAME) target.setAttribute(name, value);
}

export function unhoistAttribute(host, name) {
	const hostVariables = getHostVariables(host);
	const target = getHoistTarget(hostVariables);
	if (target.tagName === SEISMIC_HOIST_TAG_NAME) target.removeAttribute(name);
}

export function moveStyles(sourceEl, targetEl, deprioritizeStyles, onStateChange) {
	const sourceVariables = getHostVariables(sourceEl);
	const targetVariables = getHostVariables(targetEl);
	if (deprioritizeStyles) onStateChange(targetVariables[ATTACHED_ROOT_NODE], () => null);

	if (
		sourceVariables[ATTACHED_ROOT_NODE].adoptedStyleSheets &&
		sourceVariables[ATTACHED_ROOT_NODE].adoptedStyleSheets.length &&
		!targetVariables[ATTACHED_ROOT_NODE].adoptedStyleSheets.length
	) {
		targetVariables[ATTACHED_ROOT_NODE].adoptedStyleSheets = [
			...sourceVariables[ATTACHED_ROOT_NODE].adoptedStyleSheets
		]; // eslint-disable-line no-param-reassign
	} else {
		const sourceElRootNode = sourceVariables[ATTACHED_ROOT_NODE];
		const {length: sourceElRootNodeChildCount} = sourceElRootNode.children;
		if (
			sourceElRootNodeChildCount &&
			sourceElRootNode.firstChild.tagName === 'STYLE' &&
			!targetVariables[ATTACHED_ROOT_NODE].length
		) {
			targetVariables[ATTACHED_ROOT_NODE].appendChild(
				sourceElRootNode.removeChild(sourceElRootNode.firstChild)
			);
			while (sourceElRootNode.firstChild) sourceElRootNode.removeChild(sourceElRootNode.firstChild);
		}
	}
}

export function moveChildren(sourceEl, targetEl) {
	if (sourceEl.children.length) {
		const fragment = document.createDocumentFragment();

		while (sourceEl.firstChild) fragment.appendChild(sourceEl.removeChild(sourceEl.firstChild));

		targetEl.appendChild(fragment);
	}
}

function connectTarget(sourceEl, targetEl) {
	const hoistAppendToEl = getAppendToEl(sourceEl);
	if (shouldPrepend(sourceEl)) hoistAppendToEl.prepend(targetEl);
	else hoistAppendToEl.appendChild(targetEl);
}

function disconnectTarget(targetEl) {
	targetEl.parentNode.removeChild(targetEl);
}

export default function hoist(sourceEl, deprioritizeStyles, onStateChange) {
	if (
		!sourceEl[GET_RESOLVED_PROPERTY](HOIST_ATTR) &&
		!sourceEl.hasAttribute(HOIST_ATTR) &&
		!sourceEl[GET_RESOLVED_PROPERTY](NOW_HOIST_PROP) &&
		!sourceEl.hasAttribute(NOW_HOIST_ATTR)
	)
		return;

	const targetEl = createTarget(sourceEl);

	const sourceVariables = getHostVariables(sourceEl);
	const targetVariables = getHostVariables(targetEl);

	// these blocks cover the case when the hoist property is set to true after the source has already been connected and
	// possibly rendered. this appends the styles to the target shadowRoot then clears out the remaining child nodes
	// that were rendered in the source shadowRoot. it would be more efficient to never render the source shadowRoot,
	// but there isn't a way to know if a node will be hoisted in the future.
	moveStyles(sourceEl, targetEl, deprioritizeStyles, onStateChange);
	moveChildren(sourceEl, targetEl);

	connectTarget(sourceEl, targetEl);
	sourceVariables[ROOT_NODE] = targetVariables[ATTACHED_ROOT_NODE]; // eslint-disable-line no-param-reassign

	return function unhoist() {
		sourceVariables[ROOT_NODE] = sourceVariables[ATTACHED_ROOT_NODE]; // eslint-disable-line no-param-reassign
		moveStyles(targetEl, sourceEl, deprioritizeStyles, onStateChange);
		moveChildren(targetEl, sourceEl);
		disconnectTarget(targetEl);
	};
}
